type AddToCartObj = Omit<BuyerAddToCartPayload, 'promo_id' | 'parent_product_id'>;

type ExtraPayload = {
    promo_id?: string;
    parent_product_id?: string;
};

export function createAddToCartObj(cartObj: AddToCartObj, options?: ExtraPayload) {
    return {
        ...cartObj,
        ...(options || {}),
    };
}

export type CartObj = ReturnType<typeof createAddToCartObj>;

export type CreatePromoProductDataReturn = {
    payload: Record<string, ReturnType<typeof createAddToCartObj>>;
    detail: Record<string, FormattedBuyerPromoProduct>;
};

export function createPromoProductData(
    promoProductList: BuyerPromoProduct[],
    parentProduct: ReturnType<typeof createAddToCartObj>
) {
    return promoProductList.reduce<CreatePromoProductDataReturn>(
        (acc, curr) => {
            const { payload, detail } = acc;

            payload[curr.id] = createAddToCartObj(
                {
                    product_id: curr.id,
                    quantity: 1,
                },
                {
                    ...(curr.promo_id ? { promo_id: curr.promo_id } : {}),
                    parent_product_id: parentProduct?.product_id,
                }
            );

            detail[curr.id] = {
                ...curr,
                add_on_limit: curr.add_on_limit || curr.stock,
            };

            return {
                payload,
                detail,
            };
        },
        {
            payload: {},
            detail: {},
        }
    );
}

interface PriceRange {
    minAmount: number | null;
    maxAmount: number | null;
    minPrice: { value: string } | null;
    maxPrice: { value: string } | null;
}

export const getPriceRange = (
    prices: BuyerPromoProductSpecDetail[],
    priceKey: 'price' | 'add_on_price'
): string | null => {
    const result = prices?.reduce<PriceRange>(
        (acc, spec) => {
            const price = spec[priceKey];
            const amount = Number(price.amount);

            if (acc.minAmount === null || amount < acc.minAmount) {
                acc.minAmount = amount;
                acc.minPrice = price;
            }

            if (acc.maxAmount === null || amount > acc.maxAmount) {
                acc.maxAmount = amount;
                acc.maxPrice = price;
            }

            return acc;
        },
        {
            minAmount: null,
            maxAmount: null,
            minPrice: null,
            maxPrice: null,
        }
    );

    if (!result || result.minPrice === null || result.maxPrice === null) {
        return null;
    }

    if (result.minAmount === result.maxAmount) {
        return result.minPrice.value;
    }

    return `${result.minPrice.value} ~ ${result.maxPrice.value}`;
};

export const checkProductSoldOut = (product: BuyerPromoProduct, isEdit: boolean) => {
    const checkStock = (stock: number, cartQuantity: number) =>
        isEdit ? stock === 0 : stock - cartQuantity <= 0;

    if (product.spec_details.length === 0) {
        return checkStock(product.stock, product.cart_quantity);
    }

    return product.spec_details.every((spec) =>
        checkStock(spec.stock, spec.cart_quantity)
    );
};
